import React, { useState, useEffect } from 'react';
import { DiffEditor } from "@monaco-editor/react";
import { canonicalizeAndStringify } from './canonicalize';
import LoadModal from './LoadModal';
import SaveModal from './SaveModal';
import { FaFolderOpen, FaSave } from 'react-icons/fa';

function DiffChecker({
    json1,
    json2,
    keysToDelete,
    canonicalJson1,
    canonicalJson2,
    onJson1Change,
    onJson2Change,
    onKeysToDeleteChange,
    onCanonicalJson1Change,
    onCanonicalJson2Change,
    isDarkMode = false
}) {
    const [error, setError] = useState('');
    const [isEditorLoading, setIsEditorLoading] = useState(false);
    const [isJsonInputsCollapsed, setIsJsonInputsCollapsed] = useState(false);
    const [isLoadModalOpen1, setIsLoadModalOpen1] = useState(false);
    const [isSaveModalOpen1, setIsSaveModalOpen1] = useState(false);
    const [isLoadModalOpen2, setIsLoadModalOpen2] = useState(false);
    const [isSaveModalOpen2, setIsSaveModalOpen2] = useState(false);

    const handleCompare = (input1, input2, deleteKeys = keysToDelete) => {
        try {
            setError('');

            if (!input1.trim() || !input2.trim()) {
                onCanonicalJson1Change('');
                onCanonicalJson2Change('');
                setError('');
                return;
            }

            // Parse keys to delete from comma-separated string
            const keysToDeleteArray = deleteKeys
                .split(',')
                .map(key => key.trim())
                .filter(key => key.length > 0);

            // Canonicalize both JSONs
            const canonical1 = canonicalizeAndStringify(input1, keysToDeleteArray);
            const canonical2 = canonicalizeAndStringify(input2, keysToDeleteArray);

            onCanonicalJson1Change(canonical1);
            onCanonicalJson2Change(canonical2);

            // Collapse JSON inputs when comparison is done
            setIsJsonInputsCollapsed(true);
        } catch (err) {
            setError(`Error: ${err.message}`);
            onCanonicalJson1Change('');
            onCanonicalJson2Change('');
        }
    };

    const fixBackslashes = (text) => {
        try {
            // Only re-stringify if the JSON is valid but might have formatting issues
            // Don't modify valid JSON with escaped quotes
            const parsed = JSON.parse(text);
            // Only re-stringify if the original text is not already properly formatted
            const stringified = JSON.stringify(parsed);
            if (text.trim() !== stringified && text.trim() !== JSON.stringify(parsed, null, 2)) {
                return stringified;
            }
        } catch (err) {
            // If parsing fails, return original text
        }
        return text;
    };

    const handleJson1Change = (e) => {
        const newValue = fixBackslashes(e.target.value);
        onJson1Change(newValue);
        handleCompare(newValue, json2, keysToDelete);
    };

    const handleJson2Change = (e) => {
        const newValue = fixBackslashes(e.target.value);
        onJson2Change(newValue);
        handleCompare(json1, newValue, keysToDelete);
    };

    const handleKeysToDeleteChange = (e) => {
        const newValue = e.target.value;
        onKeysToDeleteChange(newValue);
        if (json1.trim() && json2.trim()) {
            handleCompare(json1, json2, newValue);
        }
    };

    const handleClear = () => {
        onJson1Change('');
        onJson2Change('');
        onCanonicalJson1Change('');
        onCanonicalJson2Change('');
        setError('');
        onKeysToDeleteChange('');
        setIsEditorLoading(false);
        setIsJsonInputsCollapsed(false);
    };

    const toggleJsonInputsCollapse = () => {
        setIsJsonInputsCollapsed(!isJsonInputsCollapsed);
    };

    const handleEditorDidMount = (editor, monaco) => {
        editor.onDidUpdateDiff(() => {
            setIsEditorLoading(false);
        });
    };

    // Effect to trigger loading when canonical JSONs change
    useEffect(() => {
        if (canonicalJson1 && canonicalJson2) {
            setIsEditorLoading(true);
        }
    }, [canonicalJson1, canonicalJson2]);

    // Effect to auto-expand diff when inputs have content
    useEffect(() => {
        if (json1.trim() && json2.trim()) {
            setIsJsonInputsCollapsed(true);
        }
    }, [json1, json2]);

    return (
        <div className="diff-checker-container">
            {/* SEO Content for Crawlers */}
            <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                <h3>JSON Compare Tool Online - Compare 2 JSON Files Free</h3>
                <p>Use our advanced JSON compare tool online to compare JSON objects, files, and schemas. Perfect for developers who need to compare 2 JSON files online or use a JSON compare tool for Windows. Our JSON diff tool provides instant, accurate comparisons with deep nested analysis.</p>
                <h4>JSON Compare Features:</h4>
                <ul>
                    <li>Compare JSON objects online free</li>
                    <li>JSON file comparison tool</li>
                    <li>Deep nested JSON diff analysis</li>
                    <li>Canonical JSON comparison</li>
                    <li>Real-time JSON validation</li>
                    <li>API response comparison</li>
                </ul>
            </div>

            <div className="keys-to-delete-section">
                <label htmlFor="keysToDelete">
                    Keys to delete (comma-separated, optional):
                </label>
                <div className="keys-input-group">
                    <input
                        id="keysToDelete"
                        type="text"
                        value={keysToDelete}
                        onChange={handleKeysToDeleteChange}
                        placeholder="List any keys you want to exclude from the comparison"
                        className="keys-input"
                    />
                    <button onClick={handleClear} className="clear-btn">
                        Clear All
                    </button>
                </div>
            </div>

            <div className={`json-inputs-container ${isJsonInputsCollapsed ? 'collapsed' : 'expanded'}`}>
                <div className="json-inputs-labels">
                    <h4>
                        JSON Object 1 - JSON Diff Tool
                        <span className="input-actions" style={{ display: 'inline-flex', gap: 8, marginLeft: 10 }}>
                            <button
                                className="action-btn load-btn"
                                title="Load"
                                onClick={() => setIsLoadModalOpen1(true)}
                            >
                                <FaFolderOpen />
                            </button>
                            <button
                                className="action-btn save-btn"
                                title="Save"
                                onClick={() => setIsSaveModalOpen1(true)}
                                disabled={!json1.trim()}
                            >
                                <FaSave />
                            </button>
                        </span>
                    </h4>
                    <h4>
                        JSON Object 2 - JSON Diff Tool
                        <span className="input-actions" style={{ display: 'inline-flex', gap: 8, marginLeft: 10 }}>
                            <button
                                className="action-btn load-btn"
                                title="Load"
                                onClick={() => setIsLoadModalOpen2(true)}
                            >
                                <FaFolderOpen />
                            </button>
                            <button
                                className="action-btn save-btn"
                                title="Save"
                                onClick={() => setIsSaveModalOpen2(true)}
                                disabled={!json2.trim()}
                            >
                                <FaSave />
                            </button>
                        </span>
                    </h4>
                    <button
                        onClick={toggleJsonInputsCollapse}
                        className="collapse-toggle-btn"
                        aria-label={isJsonInputsCollapsed ? "Expand JSON inputs" : "Collapse JSON inputs"}
                    >
                        <span className={`arrow ${isJsonInputsCollapsed ? 'collapsed' : 'expanded'}`}>
                            ▼
                        </span>
                    </button>
                </div>

                <div className="json-inputs-content">
                    <div className="json-input-group">
                        <textarea
                            value={json1}
                            onChange={handleJson1Change}
                            placeholder="Paste first JSON here and watch the diff update automatically..."
                            className="json-textarea"
                            rows={4}
                        />
                    </div>

                    <div className="json-input-group">
                        <textarea
                            value={json2}
                            onChange={handleJson2Change}
                            placeholder="Paste second JSON here and watch the diff update automatically..."
                            className="json-textarea"
                            rows={4}
                        />
                    </div>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {canonicalJson1 && canonicalJson2 && (
                <div className="diff-section">
                    <div className="diff-editor-container">
                        {isEditorLoading && (
                            <div className="loading-overlay">
                                <div className="loading-spinner"></div>
                                <p>Loading diff viewer...</p>
                            </div>
                        )}
                        <DiffEditor
                            height="100%"
                            language="json"
                            original={canonicalJson1}
                            modified={canonicalJson2}
                            keepCurrentModifiedModel={true}
                            keepCurrentOriginalModel={true}
                            theme={isDarkMode ? "vs-dark" : "vs-light"}
                            onMount={handleEditorDidMount}
                            options={{
                                renderSideBySide: true,
                                readOnly: true,
                                automaticLayout: true,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                wordWrap: 'on',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Modals for JSON 1 */}
            <LoadModal
                isOpen={isLoadModalOpen1}
                onClose={() => setIsLoadModalOpen1(false)}
                onLoadFile={(content) => {
                    const fixedContent = fixBackslashes(content);
                    onJson1Change(fixedContent);
                    handleCompare(fixedContent, json2, keysToDelete);
                }}
            />
            <SaveModal
                isOpen={isSaveModalOpen1}
                onClose={() => setIsSaveModalOpen1(false)}
                jsonContent={json1}
            />

            {/* Modals for JSON 2 */}
            <LoadModal
                isOpen={isLoadModalOpen2}
                onClose={() => setIsLoadModalOpen2(false)}
                onLoadFile={(content) => {
                    const fixedContent = fixBackslashes(content);
                    onJson2Change(fixedContent);
                    handleCompare(json1, fixedContent, keysToDelete);
                }}
            />
            <SaveModal
                isOpen={isSaveModalOpen2}
                onClose={() => setIsSaveModalOpen2(false)}
                jsonContent={json2}
            />
        </div>
    );
}

export default DiffChecker;
